import api from './api.service';

function unwrap<T>(response: { data?: any }): T {
  const d = response?.data?.data ?? response?.data;
  return d as T;
}

function asArray<T>(raw: unknown): T[] {
  return Array.isArray(raw) ? raw : [];
}

const PAYROLL_STATUS_LABEL: Record<string, string> = {
  Draft: 'Draft',
  Calculated: 'Calculated',
  Pending: 'Pending approval',
  PendingApproval: 'Pending approval',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Closed: 'Locked',
  Paid: 'Paid',
};

/** payroll_periods row → `status` display label plus the totals the payroll pages read. */
function normalizePayrollPeriod(p: any): any {
  if (!p || typeof p !== 'object') return p;
  const raw = String(p.payroll_status ?? p.status ?? '');
  return {
    ...p,
    payroll_status: raw,
    status: PAYROLL_STATUS_LABEL[raw] || raw,
    employee_count: p.total_employees ?? p.employee_count,
    total_gross: p.total_gross_salary ?? p.total_gross,
    total_deductions: p.total_deductions,
    total_net: p.total_net_salary ?? p.total_net,
    run_number: Number(p.run_number ?? 1),
    run_scope: (p.run_scope ?? 'all') as PayrollRunScope,
  };
}

export type PayrollRunScope = 'approved_timesheets' | 'all';

export const PAYROLL_RUN_SCOPE_LABEL: Record<PayrollRunScope, string> = {
  approved_timesheets: 'Approved timesheets only',
  all: 'All active employees',
};

export interface PayrollRunLine {
  code: string;
  name: string;
  type: string;
  amount: number;
  monthly?: number;
  hours?: number;
  rate?: number;
  days?: number;
  ot_kind?: string;
  source?: 'salary' | 'ot' | 'adjustment' | 'loan' | 'unpaid_leave' | 'absent';
}

export interface PayrollRegisterColumn {
  code: string;
  name: string;
  type: 'Earning' | 'Deduction';
  group: 'salary' | 'ot' | 'adjustment' | 'deduction';
}

export interface PayrollRegisterRow {
  entry_id: string;
  employee_id: string;
  employee_code: string;
  full_name: string;
  job_title: string | null;
  department_name: string | null;
  payment_method: string | null;
  bank_name: string | null;
  iban: string | null;
  timesheet_status: string | null;
  days: {
    calendar: number;
    payable: number;
    present: number;
    absent: number;
    leave: number;
    unpaid_leave: number;
    holiday: number;
    weekly_off: number;
  };
  ot_hours: number;
  monthly_salary: number;
  daily_rate: number;
  hourly_rate: number;
  amounts: Record<string, number>;
  gross: number;
  deductions: number;
  net: number;
  lines: PayrollRunLine[];
  flags: string[];
}

export interface PayrollGlLine {
  account_id: string | null;
  account_code?: string | null;
  account_name?: string | null;
  account_role: 'salary_expense' | 'salary_deduction' | 'employee_loan' | 'salary_payable';
  debit_amount: number;
  credit_amount: number;
  description: string;
  component_codes?: string[];
}

export interface PayrollGlPreview {
  gross: number;
  deductions: number;
  net: number;
  lines: PayrollGlLine[];
  total_debit: number;
  total_credit: number;
  balanced: boolean;
  configured: boolean;
  issues: string[];
  journal_voucher_id: string | null;
  journal_voucher_number: string | null;
}

export interface PayrollRegister {
  period: {
    id: string;
    period_number: string;
    period_year: number;
    period_month: number;
    status: string;
    run_scope: string;
    computed_at: string | null;
  };
  columns: PayrollRegisterColumn[];
  rows: PayrollRegisterRow[];
  totals: { employees: number; gross: number; deductions: number; net: number; by_code: Record<string, number> };
  checks: Array<{ level: 'error' | 'warning' | 'info'; message: string; employees?: string[] }>;
  gl: PayrollGlPreview;
}

export interface PayslipCompany {
  name: string;
  address: string;
  phone: string;
  email: string;
  tax_registration_number: string;
  logo: string | null;
  letterhead_header: string | null;
  letterhead_footer: string | null;
}

export interface PayslipAmountRow {
  name: string;
  detail: string;
  amount: number;
}

export interface PayrollPayslip {
  reference: string;
  period_label: string;
  period_from: string;
  period_to: string;
  payment_date: string | null;
  run_number: string;
  run_status: string;
  currency: string;
  employee: {
    id: string;
    code: string;
    name: string;
    designation: string | null;
    department: string | null;
    date_of_joining: string | null;
    nationality: string | null;
    labour_card_number: string | null;
    wps_person_code: string | null;
    pan_number?: string | null;
    uan_number?: string | null;
    esi_number?: string | null;
    ifsc?: string | null;
    payment_method: string | null;
    bank_name: string | null;
    account: string | null;
  };
  attendance: {
    calendar: number;
    payable: number;
    present: number;
    absent: number;
    leave: number;
    unpaid_leave: number;
    holiday: number;
    weekly_off: number;
    ot_hours: number;
  };
  monthly_salary: number;
  daily_rate: number;
  earnings: PayslipAmountRow[];
  deductions: PayslipAmountRow[];
  employer_contributions?: PayslipAmountRow[];
  employer_total?: number;
  gross: number;
  total_deductions: number;
  net: number;
  ytd: { months: number; gross: number; deductions: number; net: number };
  loans: Array<{ loan_number: string; loan_type: string; installment: number; outstanding: number }>;
  leave_balances: Array<{ leave_type: string; balance: number }>;
}

export interface PayslipDocument {
  company: PayslipCompany;
  slips: PayrollPayslip[];
}

export interface PayrollLabourCard {
  company_name: string | null;
  period_label: string;
  period_year: number;
  period_month: number;
  run_number: string;
  currency: string;
  employee: {
    id: string;
    file_no: string;
    name: string;
    job_title: string | null;
    department_name: string | null;
    payment_method: string | null;
    bank_name: string | null;
    iban: string | null;
  };
  days: Array<{ date: string; weekday: string; status: string | null; project_codes: string[]; ot_hours: number }>;
  salary: Array<{ code: string; name: string; monthly: number }>;
  monthly_salary: number;
  working_days: number;
  daily_rate: number;
  working_amount: number;
  ot: Array<{ kind: string; label: string; hours: number; rate: number; amount: number }>;
  additions: Array<{ name: string; amount: number }>;
  deductions: Array<{ name: string; amount: number }>;
  total_additions: number;
  total_deductions: number;
  net: number;
  show_cash_voucher: boolean;
}

export interface PagedRows<T = any> {
  rows: T[];
  total: number;
  page: number;
  limit: number;
}

/** Recruit list endpoints return `{ data, meta }` when called with `page`. */
function asPaged<T>(response: { data?: any }): PagedRows<T> {
  const body = response?.data || {};
  const rows: T[] = Array.isArray(body.data) ? body.data : [];
  const meta = body.meta || {};
  return {
    rows,
    total: Number(meta.total ?? rows.length),
    page: Number(meta.page ?? 1),
    limit: Number(meta.limit ?? rows.length),
  };
}

export interface BulkResult {
  succeeded: number;
  failed: Array<{ id: string; error: string }>;
}

export interface TimesheetGenerationInput {
  year: number;
  month: number;
  employee_ids?: string[];
  department_id?: string;
  location_id?: string;
  include_future?: boolean;
  dry_run: boolean;
}

export interface TimesheetEmployeeSummary {
  employee_id: string;
  employee_code: string;
  employee_name: string;
  outcome: 'generated' | 'skipped';
  reason?: string;
  work_week?: string;
  shift?: string;
  holiday_calendar?: string;
  from?: string;
  to?: string;
  days_created: number;
  days_updated: number;
  days_kept: number;
  days_removed: number;
  present: number;
  half_day: number;
  leave: number;
  holiday: number;
  weekly_off: number;
  absent: number;
  worked_hours: number;
  ot_hours: number;
  project_hours: number;
  warnings: string[];
}

export interface TimesheetGenerationResult {
  period: string;
  from: string;
  through: string;
  dry_run: boolean;
  employees: TimesheetEmployeeSummary[];
  totals: {
    employees: number;
    generated: number;
    skipped: number;
    days_created: number;
    days_updated: number;
    days_kept: number;
  };
  warnings: string[];
}

export interface AllocationListParams {
  employee_id?: string;
  project_id?: string;
  status?: string;
  search?: string;
}

export interface AllocationChangeResult {
  created: number;
  ended: number;
  skipped: Array<{ employee_code: string; reason: string }>;
  errors: string[];
}

export interface AllocationRow {
  id: string;
  employee_id: string;
  employee_code: string;
  employee_name: string;
  employment_status?: string;
  project_id: string | null;
  project_code: string;
  project_name?: string | null;
  project_status?: string | null;
  allocation_pct: number;
  effective_from: string;
  effective_to: string | null;
  role_on_project?: string | null;
  notes?: string | null;
  end_reason?: string | null;
  status: string;
  display_status: string;
}

function recruitListKey(name: string, params?: object): readonly unknown[] {
  return params ? ['hrms', name, params] : ['hrms', name];
}

export type WorkWeek = {
  code: string;
  label: string;
  /** Weekday numbers (0 = Sunday) that are weekly off. */
  off_days: number[];
  built_in: boolean;
};

export const hrmsKeys = {
  all: ['hrms'] as const,
  policy: () => [...hrmsKeys.all, 'policy'] as const,
  workWeeks: () => [...hrmsKeys.all, 'work-weeks'] as const,
  holidayCalendars: () => [...hrmsKeys.all, 'holiday-calendars'] as const,
  holidayDays: (calendarId: string) => [...hrmsKeys.all, 'holiday-days', calendarId] as const,
  shifts: () => [...hrmsKeys.all, 'shifts'] as const,
  salaryHeads: () => [...hrmsKeys.all, 'salary-heads'] as const,
  contracts: (employeeId?: string) => [...hrmsKeys.all, 'contracts', employeeId ?? 'all'] as const,
  contract: (id: string) => [...hrmsKeys.all, 'contract', id] as const,
  documentExpiry: (params?: object) => [...hrmsKeys.all, 'document-expiry', params] as const,
  attendanceDays: (params?: object) => [...hrmsKeys.all, 'attendance-days', params] as const,
  attendanceMonths: (params?: object) => [...hrmsKeys.all, 'attendance-months', params] as const,
  attendanceMonth: (id: string) => [...hrmsKeys.all, 'attendance-month', id] as const,
  otRequests: () => [...hrmsKeys.all, 'ot-requests'] as const,
  otRequest: (id: string) => [...hrmsKeys.all, 'ot-request', id] as const,
  leaveApplications: (params?: object) => [...hrmsKeys.all, 'leave-applications', params] as const,
  leaveApplication: (id: string) => [...hrmsKeys.all, 'leave-application', id] as const,
  leaveSettlements: (params?: object) => [...hrmsKeys.all, 'leave-settlements', params] as const,
  leaveSettlement: (id: string) => [...hrmsKeys.all, 'leave-settlement', id] as const,
  leaveTypes: () => [...hrmsKeys.all, 'leave-types'] as const,
  adjustments: (params?: object) => [...hrmsKeys.all, 'adjustments', params] as const,
  loans: () => [...hrmsKeys.all, 'loans'] as const,
  loan: (id: string) => [...hrmsKeys.all, 'loan', id] as const,
  loanSchedule: (id: string) => [...hrmsKeys.all, 'loan-schedule', id] as const,
  payrollPeriods: () => [...hrmsKeys.all, 'payroll-periods'] as const,
  payrollPeriod: (id: string) => [...hrmsKeys.all, 'payroll-period', id] as const,
  payrollPayments: (periodId?: string) => [...hrmsKeys.all, 'payroll-payments', periodId ?? 'all'] as const,
  salaryRevisions: (params?: object) => [...hrmsKeys.all, 'salary-revisions', params] as const,
  salaryRevision: (id: string) => [...hrmsKeys.all, 'salary-revision', id] as const,
  eosbSettlements: () => [...hrmsKeys.all, 'eosb-settlements'] as const,
  eosbSettlement: (id: string) => [...hrmsKeys.all, 'eosb-settlement', id] as const,
  allocations: (params?: object) => [...hrmsKeys.all, 'allocations', params] as const,
  assetIssues: (params?: object) => [...hrmsKeys.all, 'asset-issues', params] as const,
  assetIssue: (id: string) => [...hrmsKeys.all, 'asset-issue', id] as const,
  locations: () => [...hrmsKeys.all, 'locations'] as const,
  positions: () => [...hrmsKeys.all, 'positions'] as const,
  grades: () => [...hrmsKeys.all, 'grades'] as const,
  toilBank: (employeeId?: string) => [...hrmsKeys.all, 'toil-bank', employeeId ?? 'all'] as const,
  auditLog: (params?: object) => [...hrmsKeys.all, 'audit-log', params] as const,
  essSummary: () => [...hrmsKeys.all, 'ess', 'summary'] as const,
  essLeaveBalances: () => [...hrmsKeys.all, 'ess', 'leave-balances'] as const,
  essLeaveApplyContext: () => [...hrmsKeys.all, 'ess', 'leave-apply-context'] as const,
  essLeaveApplications: () => [...hrmsKeys.all, 'ess', 'leave-applications'] as const,
  essOtRequests: () => [...hrmsKeys.all, 'ess', 'ot-requests'] as const,
  essPayslips: () => [...hrmsKeys.all, 'ess', 'payslips'] as const,
  essLeaveTypes: () => [...hrmsKeys.all, 'ess', 'leave-types'] as const,
  essProfile: () => [...hrmsKeys.all, 'ess', 'profile'] as const,
  essProfileChanges: () => [...hrmsKeys.all, 'ess', 'profile-changes'] as const,
  essLetterRequests: () => [...hrmsKeys.all, 'ess', 'letter-requests'] as const,
  essTimesheets: () => [...hrmsKeys.all, 'ess', 'timesheets'] as const,
  essTimesheet: (id: string) => [...hrmsKeys.all, 'ess', 'timesheet', id] as const,
  letterRequests: (params?: object) => [...hrmsKeys.all, 'letter-requests', params ?? {}] as const,
  letterRequest: (id: string) => [...hrmsKeys.all, 'letter-request', id] as const,
  // Talent Acquisition — list keys omit empty params so `invalidateQueries(key())` prefix-matches every page/filter.
  requisitions: (params?: object) => recruitListKey('requisitions', params),
  requisition: (id: string) => [...hrmsKeys.all, 'requisition', id] as const,
  jobs: (params?: object) => recruitListKey('jobs', params),
  job: (id: string) => [...hrmsKeys.all, 'job', id] as const,
  candidates: (params?: object) => recruitListKey('candidates', params),
  candidate: (id: string) => [...hrmsKeys.all, 'candidate', id] as const,
  applications: (params?: object) => recruitListKey('applications', params),
  application: (id: string) => [...hrmsKeys.all, 'application', id] as const,
  interviews: (params?: object) => recruitListKey('interviews', params),
  offers: (params?: object) => recruitListKey('offers', params),
  offer: (id: string) => [...hrmsKeys.all, 'offer', id] as const,
  onboarding: (params?: object) => recruitListKey('onboarding', params),
  onboardingPlan: (id: string) => [...hrmsKeys.all, 'onboarding-plan', id] as const,
  careersSettings: () => [...hrmsKeys.all, 'careers-settings'] as const,
  assessmentTemplates: () => [...hrmsKeys.all, 'assessment-templates'] as const,
  assessments: (params?: object) => recruitListKey('assessments', params),
  assessment: (id: string) => [...hrmsKeys.all, 'assessment', id] as const,
  recruitHome: () => [...hrmsKeys.all, 'recruit-home'] as const,
  recruitPeople: (q?: string) => [...hrmsKeys.all, 'recruit-people', q ?? ''] as const,
  rejectionReasons: (appliesTo?: string) => [...hrmsKeys.all, 'rejection-reasons', appliesTo ?? 'all'] as const,
  recruitEmailTemplates: () => [...hrmsKeys.all, 'recruit-email-templates'] as const,
  candidateTags: () => [...hrmsKeys.all, 'candidate-tags'] as const,
  candidateComms: (id: string) => [...hrmsKeys.all, 'candidate-comms', id] as const,
  applicationBoard: (params?: object) => recruitListKey('application-board', params),
  interview: (id: string) => [...hrmsKeys.all, 'interview', id] as const,
  myInterviews: (scope: string) => [...hrmsKeys.all, 'my-interviews', scope] as const,
  interviewCriteria: (templateId?: string) => [...hrmsKeys.all, 'interview-criteria', templateId ?? 'default'] as const,
  onboardingTemplates: () => [...hrmsKeys.all, 'onboarding-templates'] as const,
  onboardingMeta: () => [...hrmsKeys.all, 'onboarding-meta'] as const,
  myOnboardingTasks: (includeClosed: boolean) => [...hrmsKeys.all, 'my-onboarding-tasks', includeClosed] as const,
};

const hrmsService = {
  // Setup
  async getPolicy(): Promise<Record<string, string>> {
    const response = await api.get('/hr/setup/policy');
    return unwrap<Record<string, string>>(response) || {};
  },
  async savePolicy(body: Record<string, string>): Promise<Record<string, string>> {
    const response = await api.put('/hr/setup/policy', body);
    return unwrap<Record<string, string>>(response) || {};
  },
  async listWorkWeeks(): Promise<WorkWeek[]> {
    const response = await api.get('/hr/setup/work-weeks');
    return asArray(unwrap(response)) as WorkWeek[];
  },
  async createWorkWeek(body: { code?: string; label?: string; off_days: number[] }): Promise<WorkWeek> {
    const response = await api.post('/hr/setup/work-weeks', body);
    return unwrap<WorkWeek>(response);
  },
  async updateWorkWeek(code: string, body: { label?: string; off_days?: number[] }): Promise<WorkWeek> {
    const response = await api.put(`/hr/setup/work-weeks/${encodeURIComponent(code)}`, body);
    return unwrap<WorkWeek>(response);
  },
  async deleteWorkWeek(code: string): Promise<void> {
    await api.delete(`/hr/setup/work-weeks/${encodeURIComponent(code)}`);
  },
  async ensureDefaults(): Promise<unknown> {
    const response = await api.post('/hr/setup/ensure-defaults');
    return unwrap(response);
  },
  async ensureApprovalWorkflows(): Promise<any> {
    const response = await api.post('/hr/setup/ensure-approval-workflows');
    return unwrap(response);
  },

  // Holidays
  async listHolidayCalendars(): Promise<any[]> {
    const response = await api.get('/hr/holidays/calendars');
    return asArray(unwrap(response));
  },
  async createHolidayCalendar(body: object): Promise<any> {
    const response = await api.post('/hr/holidays/calendars', body);
    return unwrap(response);
  },
  async updateHolidayCalendar(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/holidays/calendars/${id}`, body);
    return unwrap(response);
  },
  async deleteHolidayCalendar(id: string): Promise<void> {
    await api.delete(`/hr/holidays/calendars/${id}`);
  },
  async listHolidayDays(calendarId: string): Promise<any[]> {
    const response = await api.get(`/hr/holidays/calendars/${calendarId}/days`);
    return asArray(unwrap(response));
  },
  async createHolidayDay(calendarId: string, body: object): Promise<any> {
    const response = await api.post(`/hr/holidays/calendars/${calendarId}/days`, body);
    return unwrap(response);
  },
  async updateHolidayDay(calendarId: string, dayId: string, body: object): Promise<any> {
    const response = await api.put(`/hr/holidays/calendars/${calendarId}/days/${dayId}`, body);
    return unwrap(response);
  },
  async deleteHolidayDay(calendarId: string, dayId: string): Promise<void> {
    await api.delete(`/hr/holidays/calendars/${calendarId}/days/${dayId}`);
  },

  // Shifts
  async listShifts(): Promise<any[]> {
    const response = await api.get('/hr/shifts');
    return asArray(unwrap(response));
  },
  async createShift(body: object): Promise<any> {
    const response = await api.post('/hr/shifts', body);
    return unwrap(response);
  },
  async updateShift(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/shifts/${id}`, body);
    return unwrap(response);
  },
  async deleteShift(id: string): Promise<void> {
    await api.delete(`/hr/shifts/${id}`);
  },

  // Salary heads
  async listSalaryHeads(): Promise<any[]> {
    const response = await api.get('/hr/salary-heads');
    return asArray(unwrap(response));
  },
  async createSalaryHead(body: object): Promise<any> {
    const response = await api.post('/hr/salary-heads', body);
    return unwrap(response);
  },
  async updateSalaryHead(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/salary-heads/${id}`, body);
    return unwrap(response);
  },
  async deleteSalaryHead(id: string): Promise<void> {
    await api.delete(`/hr/salary-heads/${id}`);
  },

  // Contracts
  async listContracts(params?: { employee_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/contracts', { params });
    return asArray(unwrap(response));
  },
  async getContract(id: string): Promise<any> {
    const response = await api.get(`/hr/contracts/${id}`);
    return unwrap(response);
  },
  async createContract(body: object): Promise<any> {
    const response = await api.post('/hr/contracts', body);
    return unwrap(response);
  },
  async submitContract(id: string): Promise<any> {
    const response = await api.post(`/hr/contracts/${id}/submit`);
    return unwrap(response);
  },

  // Document expiry
  async listDocumentExpiry(params?: { expiring_soon?: boolean; days?: number }): Promise<any[]> {
    const response = await api.get('/hr/document-expiry', { params });
    return asArray(unwrap(response));
  },

  // Attendance
  async listAttendanceDays(params?: { date?: string; from?: string; to?: string; employee_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/attendance/days', { params });
    return asArray(unwrap(response));
  },
  async upsertAttendanceDay(body: object): Promise<any> {
    const response = await api.post('/hr/attendance/days', body);
    return unwrap(response);
  },
  async downloadAttendanceTemplateAsFile(): Promise<void> {
    const client = api.getClient();
    const response = await client.get('/hr/attendance/excel/template', { responseType: 'blob' });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'attendance-timesheet-import-template.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  },
  async exportAttendanceExcelBlob(params?: {
    date?: string;
    from?: string;
    to?: string;
    employee_id?: string;
  }): Promise<Blob> {
    const client = api.getClient();
    const response = await client.get('/hr/attendance/excel/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  async importAttendanceExcel(
    file: File
  ): Promise<{ created: number; updated: number; errors: string[]; skipped: number }> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/hr/attendance/excel/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(response);
  },
  async generateTimesheets(body: TimesheetGenerationInput): Promise<TimesheetGenerationResult> {
    const response = await api.post('/hr/attendance/months/generate', body);
    return unwrap(response);
  },
  async consolidateAttendanceMonths(body: {
    year: number;
    month: number;
    employee_ids?: string[];
  }): Promise<{ updated: number; skipped: string[] }> {
    const response = await api.post('/hr/attendance/months/consolidate', body);
    return unwrap(response);
  },
  async recalculateAttendanceMonth(id: string): Promise<any> {
    const response = await api.post(`/hr/attendance/months/${id}/recalculate`);
    return unwrap(response);
  },
  async reopenAttendanceMonth(id: string, reason: string): Promise<any> {
    const response = await api.post(`/hr/attendance/months/${id}/reopen`, { reason });
    return unwrap(response);
  },
  async submitAttendanceMonthsBulk(ids: string[]): Promise<{ submitted: number; errors: string[] }> {
    const response = await api.post('/hr/attendance/months/submit-bulk', { ids });
    return unwrap(response);
  },
  async listAttendanceMonths(params?: {
    year?: number;
    month?: number;
    employee_id?: string;
    status?: string;
  }): Promise<any[]> {
    const response = await api.get('/hr/attendance/months', { params });
    return asArray(unwrap(response));
  },
  async getAttendanceMonth(id: string): Promise<any> {
    const response = await api.get(`/hr/attendance/months/${id}`);
    return unwrap(response);
  },
  async submitAttendanceMonth(id: string): Promise<any> {
    const response = await api.post(`/hr/attendance/months/${id}/submit`);
    return unwrap(response);
  },

  // OT
  async listOtRequests(): Promise<any[]> {
    const response = await api.get('/hr/ot-requests');
    return asArray(unwrap(response));
  },
  async getOtRequest(id: string): Promise<any> {
    const response = await api.get(`/hr/ot-requests/${id}`);
    return unwrap(response);
  },
  async createOtRequest(body: object): Promise<any> {
    const response = await api.post('/hr/ot-requests', body);
    return unwrap(response);
  },
  async submitOt(id: string): Promise<any> {
    const response = await api.post(`/hr/ot-requests/${id}/submit`);
    return unwrap(response);
  },
  async approveOtRequest(id: string, body?: object): Promise<any> {
    const response = await api.post(`/hr/ot-requests/${id}/approve`, body);
    return unwrap(response);
  },

  // Leave
  async listLeaveTypes(): Promise<any[]> {
    const response = await api.get('/hr/leave-types');
    return asArray(unwrap(response));
  },
  async createLeaveType(body: object): Promise<any> {
    const response = await api.post('/hr/leave-types', body);
    return unwrap(response);
  },
  async updateLeaveType(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/leave-types/${id}`, body);
    return unwrap(response);
  },
  async deleteLeaveType(id: string): Promise<void> {
    await api.delete(`/hr/leave-types/${id}`);
  },
  async listCompanyLeaveBalances(params?: { leave_year?: number; leave_type?: string }): Promise<any[]> {
    const response = await api.get('/hr/leave-balances', { params });
    return asArray(unwrap(response));
  },
  async bulkUpsertLeaveOpenings(body: {
    leave_year: number;
    leave_type: string;
    rows: Array<{ employee_id: string; opening_balance: number; carried_forward?: number; consumed?: number }>;
  }): Promise<any> {
    const response = await api.post('/hr/leave-balances/bulk-openings', body);
    return unwrap(response);
  },
  async listLeaveApplications(params?: { status?: string; employee_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/leave-applications', { params });
    return asArray(unwrap(response));
  },
  async getLeaveApplication(id: string): Promise<any> {
    const response = await api.get(`/hr/leave-applications/${id}`);
    return unwrap(response);
  },
  async createLeaveApplication(body: object): Promise<any> {
    const response = await api.post('/hr/leave-applications', body);
    return unwrap(response);
  },
  async updateLeaveApplication(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/leave-applications/${id}`, body);
    return unwrap(response);
  },
  async submitLeave(id: string): Promise<any> {
    const response = await api.post(`/hr/leave-applications/${id}/submit`);
    return unwrap(response);
  },
  async approveLeave(id: string): Promise<any> {
    const response = await api.post(`/hr/leave-applications/${id}/approve`);
    return unwrap(response);
  },
  async rejectLeave(id: string, body?: object): Promise<any> {
    const response = await api.post(`/hr/leave-applications/${id}/reject`, body);
    return unwrap(response);
  },
  async dutyResumeLeave(id: string, body: { duty_resume_date: string }): Promise<any> {
    const response = await api.post(`/hr/leave-applications/${id}/duty-resume`, body);
    return unwrap(response);
  },
  async extendLeave(id: string, body: { to_date: string; reason?: string; submit?: boolean }): Promise<any> {
    const response = await api.post(`/hr/leave-applications/${id}/extend`, body);
    return unwrap(response);
  },
  async bulkAbsence(body: object): Promise<any> {
    const response = await api.post('/hr/leave-applications/bulk', body);
    return unwrap(response);
  },

  // Leave settlements (UAE/MENA annual leave pay)
  async listLeaveSettlements(params?: {
    status?: string;
    employee_id?: string;
    leave_application_id?: string;
  }): Promise<any[]> {
    const response = await api.get('/hr/leave-settlements', { params });
    return asArray(unwrap(response));
  },
  async getLeaveSettlement(id: string): Promise<any> {
    const response = await api.get(`/hr/leave-settlements/${id}`);
    return unwrap(response);
  },
  async calculateLeaveSettlement(body: { leave_application_id: string }): Promise<any> {
    const response = await api.post('/hr/leave-settlements/calculate', body);
    return unwrap(response);
  },
  async createLeaveSettlement(body: {
    leave_application_id: string;
    eligible_days?: number;
    other_amount?: number;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/hr/leave-settlements', body);
    return unwrap(response);
  },
  async updateLeaveSettlement(
    id: string,
    body: {
      eligible_days?: number;
      other_amount?: number;
      settlement_mode?: 'Payroll' | 'SalaryAdvance' | null;
      target_period_year?: number;
      target_period_month?: number;
      advance_installments?: number;
      notes?: string;
    }
  ): Promise<any> {
    const response = await api.put(`/hr/leave-settlements/${id}`, body);
    return unwrap(response);
  },
  async submitLeaveSettlement(id: string): Promise<any> {
    const response = await api.post(`/hr/leave-settlements/${id}/submit`);
    return unwrap(response);
  },
  async approveLeaveSettlement(id: string, body?: { remarks?: string }): Promise<any> {
    const response = await api.post(`/hr/leave-settlements/${id}/approve`, body || {});
    return unwrap(response);
  },
  async rejectLeaveSettlement(id: string, body?: object): Promise<any> {
    const response = await api.post(`/hr/leave-settlements/${id}/reject`, body || {});
    return unwrap(response);
  },
  async bookLeaveSettlement(
    id: string,
    body: {
      settlement_mode: 'Payroll' | 'SalaryAdvance';
      target_period_year?: number;
      target_period_month?: number;
      advance_installments?: number;
      create_recovery_loan?: boolean;
      mark_advance_paid?: boolean;
      expense_account_id?: string;
      payable_account_id?: string;
      department_id?: string | null;
    }
  ): Promise<any> {
    const response = await api.post(`/hr/leave-settlements/${id}/book`, body);
    return unwrap(response);
  },
  async getLeaveSettlementGlDefaults(): Promise<{
    expense_account_id: string | null;
    payable_account_id: string | null;
  }> {
    const response = await api.get('/hr/leave-settlements/gl-defaults');
    return unwrap(response);
  },
  async listPayableLeaveSettlements(params?: { employee_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/leave-settlements/payable-for-payment', { params });
    return asArray(unwrap(response));
  },
  async markLeaveSettlementPaid(id: string): Promise<any> {
    const response = await api.post(`/hr/leave-settlements/${id}/mark-paid`);
    return unwrap(response);
  },

  // Adjustments
  async listAdjustments(params?: { period_year?: number; period_month?: number; employee_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/adjustments', { params });
    return asArray(unwrap(response));
  },
  async createAdjustment(body: object): Promise<any> {
    const response = await api.post('/hr/adjustments', body);
    return unwrap(response);
  },
  async deleteAdjustment(id: string): Promise<void> {
    await api.delete(`/hr/adjustments/${id}`);
  },

  // Loans
  async listLoans(): Promise<any[]> {
    const response = await api.get('/hr/loans');
    return asArray(unwrap(response));
  },
  async getLoan(id: string): Promise<any> {
    const response = await api.get(`/hr/loans/${id}`);
    return unwrap(response);
  },
  async getLoanSchedule(id: string): Promise<any[]> {
    const response = await api.get(`/hr/loans/${id}/schedule`);
    return asArray(unwrap(response));
  },
  async createLoan(body: object): Promise<any> {
    const response = await api.post('/hr/loans', body);
    return unwrap(response);
  },
  async submitLoan(id: string): Promise<any> {
    const response = await api.post(`/hr/loans/${id}/submit`);
    return unwrap(response);
  },

  // Payroll
  async listPayrollPeriods(): Promise<any[]> {
    const response = await api.get('/hr/payroll/periods');
    return asArray(unwrap(response)).map(normalizePayrollPeriod);
  },
  async getPayrollPeriod(id: string): Promise<any> {
    const response = await api.get(`/hr/payroll/periods/${id}`);
    return normalizePayrollPeriod(unwrap(response));
  },
  async approvePayrollPeriod(id: string): Promise<any> {
    const response = await api.post(`/hr/payroll/periods/${id}/approve`);
    return unwrap(response);
  },
  async payrollGlHandoff(id: string): Promise<PayrollGlPreview & { message: string }> {
    const response = await api.post(`/hr/payroll/periods/${id}/gl-handoff`);
    return unwrap(response);
  },
  async getPayrollRegister(id: string): Promise<PayrollRegister> {
    const response = await api.get(`/hr/payroll/periods/${id}/register`);
    return unwrap(response);
  },
  async downloadPayrollRegister(id: string, filename?: string): Promise<void> {
    const response = await api.getClient().get(`/hr/payroll/periods/${id}/register/export`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    const match = /filename="?([^";]+)"?/i.exec(String(response.headers?.['content-disposition'] || ''));
    a.download = match?.[1] || filename || `salary-register-${id}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },
  async getPayrollPayslips(id: string, employeeId?: string): Promise<PayslipDocument> {
    const response = await api.get(`/hr/payroll/periods/${id}/payslips`, {
      params: employeeId ? { employee_id: employeeId } : undefined,
    });
    return unwrap(response);
  },
  async getEssPayslipDocument(periodId: string): Promise<PayslipDocument> {
    const response = await api.get('/hr/me/payslips/print', { params: { period_id: periodId } });
    return unwrap(response);
  },
  async getPayrollLabourCards(id: string, employeeId?: string): Promise<PayrollLabourCard[]> {
    const response = await api.get(`/hr/payroll/periods/${id}/labour-cards`, {
      params: employeeId ? { employee_id: employeeId } : undefined,
    });
    return asArray(unwrap(response)) as PayrollLabourCard[];
  },
  async openPayrollPeriod(body: {
    period_year: number;
    period_month: number;
    department_id: string;
    run_scope: PayrollRunScope;
  }): Promise<any> {
    const response = await api.post('/hr/payroll/periods', body);
    return unwrap(response);
  },
  async deletePayrollPeriod(id: string): Promise<any> {
    const response = await api.delete(`/hr/payroll/periods/${id}`);
    return unwrap(response);
  },
  async submitPayrollPeriod(id: string): Promise<any> {
    const response = await api.post(`/hr/payroll/periods/${id}/submit`);
    return unwrap(response);
  },
  async computePayrollPeriod(id: string): Promise<any> {
    const response = await api.post(`/hr/payroll/periods/${id}/compute`, undefined, { timeout: 300_000 });
    return unwrap(response);
  },
  async lockPayrollPeriod(id: string): Promise<any> {
    const response = await api.post(`/hr/payroll/periods/${id}/lock`);
    return unwrap(response);
  },
  async listPayrollPayments(params?: { payroll_period_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/payroll/payments', { params });
    return asArray(unwrap(response));
  },
  async createPayrollPayment(body: object): Promise<any> {
    const response = await api.post('/hr/payroll/payments', body);
    return unwrap(response);
  },
  async deletePayrollPayment(id: string): Promise<any> {
    const response = await api.delete(`/hr/payroll/payments/${id}`);
    return unwrap(response);
  },
  async exportWps(periodId: string): Promise<string> {
    const response = await api.get(`/hr/payroll/periods/${periodId}/wps-export`, {
      responseType: 'text',
    });
    return (response?.data as string) || '';
  },
  /** Download WPS file as a blob (preferred for save-as). Returns skip metadata from headers. */
  async downloadWpsExport(
    periodId: string,
    filename?: string
  ): Promise<{ included: number; skippedCount: number; skippedDetail: string }> {
    const client = api.getClient();
    const response = await client.get(`/hr/payroll/periods/${periodId}/wps-export`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = String(response.headers?.['content-disposition'] || '');
    const match = /filename="?([^";]+)"?/i.exec(cd);
    a.download = match?.[1] || filename || `wps-${periodId}.sif`;
    a.click();
    URL.revokeObjectURL(url);
    const included = Number(response.headers?.['x-wps-included'] || 0);
    const skippedCount = Number(response.headers?.['x-wps-skipped-count'] || 0);
    const skippedDetail = String(response.headers?.['x-wps-skipped'] || '');
    return { included, skippedCount, skippedDetail };
  },
  /** India NEFT/IMPS bank credit CSV download. */
  async downloadNeftExport(
    periodId: string,
    filename?: string
  ): Promise<{ included: number; skippedCount: number; skippedDetail: string }> {
    const client = api.getClient();
    const response = await client.get(`/hr/payroll/periods/${periodId}/neft-export`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = String(response.headers?.['content-disposition'] || '');
    const match = /filename="?([^";]+)"?/i.exec(cd);
    a.download = match?.[1] || filename || `neft-${periodId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    const included = Number(response.headers?.['x-neft-included'] || 0);
    const skippedCount = Number(response.headers?.['x-neft-skipped-count'] || 0);
    const skippedDetail = String(response.headers?.['x-neft-skipped'] || '');
    return { included, skippedCount, skippedDetail };
  },
  async markPayrollPaymentPaid(id: string, body?: object): Promise<any> {
    const response = await api.post(`/hr/payroll/payments/${id}/mark-paid`, body || {});
    return unwrap(response);
  },

  // Salary revisions
  async listSalaryRevisions(params?: { employee_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/salary-revisions', { params });
    return asArray(unwrap(response));
  },
  async getSalaryRevision(id: string): Promise<any> {
    const response = await api.get(`/hr/salary-revisions/${id}`);
    return unwrap(response);
  },
  async createSalaryRevision(body: object): Promise<any> {
    const response = await api.post('/hr/salary-revisions', body);
    return unwrap(response);
  },
  async updateSalaryRevision(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/salary-revisions/${id}`, body);
    return unwrap(response);
  },
  async submitSalaryRevision(id: string): Promise<any> {
    const response = await api.post(`/hr/salary-revisions/${id}/submit`);
    return unwrap(response);
  },
  async applySalaryRevision(id: string): Promise<any> {
    const response = await api.post(`/hr/salary-revisions/${id}/apply`);
    return unwrap(response);
  },
  async downloadSalaryRevisionTemplateBlob(): Promise<Blob> {
    const response = await api.getClient().get('/hr/salary-revisions/excel/template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  async exportSalaryRevisionsBlob(params?: {
    employee_id?: string;
    status?: string;
  }): Promise<Blob> {
    const response = await api.getClient().get('/hr/salary-revisions/excel/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  async importSalaryRevisionsExcel(file: File): Promise<{
    created: number;
    submitted: number;
    revision_numbers: string[];
    errors: string[];
  }> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/hr/salary-revisions/excel/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(response);
  },

  // EOSB
  async listEosbSettlements(): Promise<any[]> {
    const response = await api.get('/hr/eosb/settlements');
    return asArray(unwrap(response));
  },
  async getEosbSettlement(id: string): Promise<any> {
    const response = await api.get(`/hr/eosb/settlements/${id}`);
    return unwrap(response);
  },
  async getEosbEmployeeProfile(employeeId: string, params?: { as_of_date?: string }): Promise<any> {
    const response = await api.get(`/hr/eosb/employees/${employeeId}/profile`, { params });
    return unwrap(response);
  },
  async eosbForecast(params?: {
    as_of_date?: string;
    horizon_months?: number;
    salary_growth_pct?: number;
    employee_id?: string;
  }): Promise<any> {
    const response = await api.get('/hr/eosb/forecast', { params });
    return unwrap(response);
  },
  async calculateEosb(body: object): Promise<any> {
    const response = await api.post('/hr/eosb/calculate', body);
    return unwrap(response);
  },
  async createEosbSettlement(body: object): Promise<any> {
    const response = await api.post('/hr/eosb/settlements', body);
    return unwrap(response);
  },
  async submitEosb(id: string): Promise<any> {
    const response = await api.post(`/hr/eosb/settlements/${id}/submit`);
    return unwrap(response);
  },
  async saveEosbChecklist(id: string, checklist: Record<string, boolean>): Promise<any> {
    const response = await api.post(`/hr/eosb/settlements/${id}/checklist`, { checklist });
    return unwrap(response);
  },
  async markEosbPaid(id: string, body?: object): Promise<any> {
    const response = await api.post(`/hr/eosb/settlements/${id}/mark-paid`, body || {});
    return unwrap(response);
  },

  // TOIL
  async listToilBank(params?: { employee_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/ot/toil-bank', { params });
    return asArray(unwrap(response));
  },
  async consumeToil(body: {
    employee_id: string;
    hours: number;
    consume_date: string;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/hr/ot/toil-consume', body);
    return unwrap(response);
  },

  // Accruals
  async runLeaveYearEndCarry(body: { year: number }): Promise<any> {
    const response = await api.post('/hr/accruals/leave/year-end-carry', body);
    return unwrap(response);
  },
  async runLeaveAccrual(body?: {
    year?: number;
    month?: number;
    dry_run?: boolean;
    preview?: boolean;
  }): Promise<any> {
    const response = await api.post('/hr/accruals/leave/run', body || {});
    return unwrap(response);
  },

  async previewLeaveAccrual(body?: { year?: number; month?: number }): Promise<any> {
    const response = await api.post('/hr/accruals/leave/preview', body || {});
    return unwrap(response);
  },

  async leaveAccrualDashboard(params?: { year?: number; month?: number }): Promise<any> {
    const response = await api.get('/hr/accruals/leave/dashboard', { params });
    return unwrap(response);
  },

  async employeeLeaveAccrualLedger(employeeId: string, year?: number): Promise<any> {
    const response = await api.get(`/hr/accruals/leave/employees/${employeeId}/ledger`, {
      params: year != null ? { year } : undefined,
    });
    return unwrap(response);
  },

  async runEosbAccrual(body?: { as_of_date?: string }): Promise<any> {
    const response = await api.post('/hr/accruals/eosb/run', body || {});
    return unwrap(response);
  },

  async eosbAccrualDashboard(params?: { as_of_date?: string }): Promise<any> {
    const response = await api.get('/hr/accruals/eosb/dashboard', { params });
    return unwrap(response);
  },

  async listAccrualBatches(params?: { kind?: 'LEAVE' | 'EOSB'; limit?: number }): Promise<any[]> {
    const response = await api.get('/hr/accruals/batches', { params });
    return asArray(unwrap(response));
  },

  async accrualAccountingConfig(): Promise<any> {
    const response = await api.get('/hr/accruals/accounting/config');
    return unwrap(response);
  },
  async valueAccrualBatch(batchId: string): Promise<any> {
    const response = await api.post(`/hr/accruals/batches/${batchId}/value`);
    return unwrap(response);
  },
  async previewAccrualGl(batchId: string): Promise<any> {
    const response = await api.get(`/hr/accruals/batches/${batchId}/gl-preview`);
    return unwrap(response);
  },
  async createAccrualGlHandoff(batchId: string): Promise<any> {
    const response = await api.post(`/hr/accruals/batches/${batchId}/gl-handoff`);
    return unwrap(response);
  },
  async submitAccrualAccounting(batchId: string): Promise<any> {
    const response = await api.post(`/hr/accruals/batches/${batchId}/submit-accounting`);
    return unwrap(response);
  },
  async approveAccrualAccounting(batchId: string, body?: { remarks?: string }): Promise<any> {
    const response = await api.post(`/hr/accruals/batches/${batchId}/approve-accounting`, body || {});
    return unwrap(response);
  },
  async rejectAccrualAccounting(
    batchId: string,
    body: { rejection_reason_code?: string; remarks?: string }
  ): Promise<any> {
    const response = await api.post(`/hr/accruals/batches/${batchId}/reject-accounting`, body);
    return unwrap(response);
  },
  async cancelAccrualAccounting(batchId: string): Promise<any> {
    const response = await api.post(`/hr/accruals/batches/${batchId}/cancel-accounting`);
    return unwrap(response);
  },
  async accrualReconciliationDrilldown(runId: string): Promise<any> {
    const response = await api.get(`/hr/accruals/reconciliation/${runId}/drilldown`);
    return unwrap(response);
  },
  async createEosbSettlementGlHandoff(settlementId: string): Promise<any> {
    const response = await api.post(`/hr/eosb/settlements/${settlementId}/gl-handoff`);
    return unwrap(response);
  },
  async syncAccrualBatchFinance(batchId: string): Promise<any> {
    const response = await api.post(`/hr/accruals/batches/${batchId}/sync-finance`);
    return unwrap(response);
  },
  async listBenefitValuationMovements(params?: {
    batch_id?: string;
    benefit_type?: string;
    limit?: number;
  }): Promise<any[]> {
    const response = await api.get('/hr/accruals/valuation-movements', { params });
    return asArray(unwrap(response));
  },
  async runAccrualReconciliation(body?: {
    benefit_type?: 'LEAVE' | 'EOSB' | 'ALL';
    as_of_date?: string;
  }): Promise<any> {
    const response = await api.post('/hr/accruals/reconciliation/run', body || {});
    return unwrap(response);
  },
  async listAccrualReconciliations(params?: { limit?: number }): Promise<any[]> {
    const response = await api.get('/hr/accruals/reconciliation', { params });
    return asArray(unwrap(response));
  },
  async signOffAccrualReconciliation(runId: string): Promise<any> {
    const response = await api.post(`/hr/accruals/reconciliation/${runId}/sign-off`);
    return unwrap(response);
  },
  async accrualCloseGate(params?: { as_of_date?: string }): Promise<any> {
    const response = await api.get('/hr/accruals/close-gate', { params });
    return unwrap(response);
  },
  async benefitComplianceScan(): Promise<any> {
    const response = await api.post('/hr/accruals/compliance/scan');
    return unwrap(response);
  },
  async listBenefitComplianceRules(params?: { jurisdiction?: string }): Promise<any[]> {
    const response = await api.get('/hr/accruals/compliance/rules', { params });
    return asArray(unwrap(response));
  },

  // Locations
  async listLocations(): Promise<any[]> {
    const response = await api.get('/hr/locations');
    return asArray(unwrap(response));
  },
  async createLocation(body: object): Promise<any> {
    const response = await api.post('/hr/locations', body);
    return unwrap(response);
  },
  async updateLocation(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/locations/${id}`, body);
    return unwrap(response);
  },
  async downloadLocationTemplateBlob(): Promise<Blob> {
    const response = await api.getClient().get('/hr/locations/excel/template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  async exportLocationsExcelBlob(): Promise<Blob> {
    const response = await api.getClient().get('/hr/locations/excel/export', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  async importLocationsExcel(
    file: File
  ): Promise<{ created: number; updated: number; errors: string[]; skipped: number }> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/hr/locations/excel/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(response);
  },

  // Positions
  async listPositions(): Promise<any[]> {
    const response = await api.get('/hr/positions');
    return asArray(unwrap(response));
  },
  async createPosition(body: object): Promise<any> {
    const response = await api.post('/hr/positions', body);
    return unwrap(response);
  },
  async updatePosition(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/positions/${id}`, body);
    return unwrap(response);
  },
  async deletePosition(id: string): Promise<void> {
    await api.delete(`/hr/positions/${id}`);
  },

  // Grades
  async listGrades(): Promise<any[]> {
    const response = await api.get('/hr/grades');
    return asArray(unwrap(response));
  },
  async createGrade(body: object): Promise<any> {
    const response = await api.post('/hr/grades', body);
    return unwrap(response);
  },
  async updateGrade(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/grades/${id}`, body);
    return unwrap(response);
  },
  async deleteGrade(id: string): Promise<void> {
    await api.delete(`/hr/grades/${id}`);
  },

  // Employee lifecycle
  async confirmEmployee(id: string, body?: { confirmation_date?: string }): Promise<any> {
    const response = await api.post(`/hr/employees/${id}/confirm`, body || {});
    return unwrap(response);
  },
  async exitEmployee(
    id: string,
    body: { exit_type: string; termination_date: string; termination_reason?: string }
  ): Promise<any> {
    const response = await api.post(`/hr/employees/${id}/exit`, body);
    return unwrap(response);
  },

  // Audit log
  async listAuditLog(params?: { area?: string }): Promise<any[]> {
    const response = await api.get('/hr/audit-log', { params });
    return asArray(unwrap(response));
  },

  // ESS (employee self-service)
  async getEssSummary(): Promise<any> {
    const response = await api.get('/hr/me/summary');
    return unwrap(response);
  },
  async listEssLeaveBalances(): Promise<any[]> {
    const response = await api.get('/hr/me/leave-balances');
    return asArray(unwrap(response));
  },
  async listEssLeaveApplications(): Promise<any[]> {
    const response = await api.get('/hr/me/leave-applications');
    return asArray(unwrap(response));
  },
  async getEssLeaveApplyContext(params?: { days?: number }): Promise<any> {
    const response = await api.get('/hr/me/leave-apply-context', { params });
    return unwrap(response);
  },
  async createEssLeaveApplication(body: object): Promise<any> {
    const response = await api.post('/hr/me/leave-applications', body);
    return unwrap(response);
  },
  async submitEssLeaveApplication(id: string): Promise<any> {
    const response = await api.post(`/hr/me/leave-applications/${id}/submit`);
    return unwrap(response);
  },
  async listEssOtRequests(): Promise<any[]> {
    const response = await api.get('/hr/me/ot-requests');
    return asArray(unwrap(response));
  },
  async createEssOtRequest(body: object): Promise<any> {
    const response = await api.post('/hr/me/ot-requests', body);
    return unwrap(response);
  },
  async submitEssOtRequest(id: string): Promise<any> {
    const response = await api.post(`/hr/me/ot-requests/${id}/submit`);
    return unwrap(response);
  },
  async listEssPayslips(): Promise<any[]> {
    const response = await api.get('/hr/me/payslips');
    return asArray(unwrap(response));
  },
  async getEssPayslip(params: { period_id: string }): Promise<any> {
    const response = await api.get('/hr/me/payslips', { params });
    const data = unwrap<any>(response);
    if (Array.isArray(data)) {
      return data.find((p: any) => p.period_id === params.period_id || p.id === params.period_id) || data[0];
    }
    return data;
  },
  async listEssLeaveTypes(): Promise<any[]> {
    const response = await api.get('/hr/me/leave-types');
    return asArray(unwrap(response));
  },
  async getEssProfile(): Promise<any> {
    const response = await api.get('/hr/me/profile');
    return unwrap(response);
  },
  async listEssProfileChangeRequests(): Promise<any[]> {
    const response = await api.get('/hr/me/profile-change-requests');
    return asArray(unwrap(response));
  },
  async createEssProfileChangeRequest(body: object): Promise<any> {
    const response = await api.post('/hr/me/profile-change-requests', body);
    return unwrap(response);
  },
  async listEssLetterRequests(): Promise<any[]> {
    const response = await api.get('/hr/me/letter-requests');
    return asArray(unwrap(response));
  },
  async createEssLetterRequest(body: object): Promise<any> {
    const response = await api.post('/hr/me/letter-requests', body);
    return unwrap(response);
  },
  async downloadEssLetterPdf(id: string): Promise<Blob> {
    const response = await api.get(`/hr/me/letter-requests/${id}/pdf`, { responseType: 'blob' });
    return response.data as Blob;
  },
  async listEssTimesheets(params?: { year?: number }): Promise<any[]> {
    const response = await api.get('/hr/me/timesheets', { params });
    return asArray(unwrap(response));
  },
  async getEssTimesheet(id: string): Promise<any> {
    const response = await api.get(`/hr/me/timesheets/${id}`);
    return unwrap(response);
  },
  async listLetterRequests(params?: { status?: string }): Promise<any[]> {
    const response = await api.get('/hr/letter-requests', { params });
    return asArray(unwrap(response));
  },
  async getLetterRequest(id: string): Promise<any> {
    const response = await api.get(`/hr/letter-requests/${id}`);
    return unwrap(response);
  },
  async approveLetterRequest(id: string, remarks?: string): Promise<any> {
    const response = await api.post(`/hr/letter-requests/${id}/approve`, { remarks });
    return unwrap(response);
  },
  async rejectLetterRequest(id: string, body: object): Promise<any> {
    const response = await api.post(`/hr/letter-requests/${id}/reject`, body);
    return unwrap(response);
  },
  async downloadLetterRequestPdf(id: string): Promise<Blob> {
    const response = await api.get(`/hr/letter-requests/${id}/pdf`, { responseType: 'blob' });
    return response.data as Blob;
  },

  // Allocations
  async listAllocations(params?: AllocationListParams): Promise<any[]> {
    const response = await api.get('/hr/allocations', { params });
    return asArray(unwrap(response));
  },
  async createAllocation(body: object): Promise<any> {
    const response = await api.post('/hr/allocations', body);
    return unwrap(response);
  },
  async bulkAllocate(body: {
    employee_ids: string[];
    project_id: string;
    allocation_pct?: number | null;
    effective_from: string;
    effective_to?: string | null;
    role_on_project?: string | null;
    notes?: string | null;
    mode: 'add' | 'replace' | 'move';
    from_project_id?: string | null;
  }): Promise<AllocationChangeResult> {
    const response = await api.post('/hr/allocations/bulk', body);
    return unwrap(response);
  },
  async updateAllocation(
    id: string,
    body: { allocation_pct?: number; effective_to?: string | null; role_on_project?: string | null; notes?: string | null }
  ): Promise<any> {
    const response = await api.put(`/hr/allocations/${id}`, body);
    return unwrap(response);
  },
  async endAllocation(id: string, body: { effective_to?: string; reason?: string }): Promise<any> {
    const response = await api.post(`/hr/allocations/${id}/end`, body);
    return unwrap(response);
  },
  async deleteAllocation(id: string): Promise<void> {
    await api.delete(`/hr/allocations/${id}`);
  },
  async downloadAllocationTemplateBlob(): Promise<Blob> {
    const response = await api.getClient().get('/hr/allocations/excel/template', { responseType: 'blob' });
    return response.data as Blob;
  },
  async exportAllocationsBlob(params?: AllocationListParams): Promise<Blob> {
    const response = await api.getClient().get('/hr/allocations/excel/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  async importAllocationsExcel(file: File): Promise<AllocationChangeResult> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/hr/allocations/excel/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(response);
  },

  // Asset custody
  async listAssetIssues(params?: { employee_id?: string; status?: string }): Promise<any[]> {
    const response = await api.get('/hr/asset-issues', { params });
    return asArray(unwrap(response));
  },
  async getAssetIssue(id: string): Promise<any> {
    const response = await api.get(`/hr/asset-issues/${id}`);
    return unwrap(response);
  },
  async issueAsset(body: object): Promise<any> {
    const response = await api.post('/hr/asset-issues', body);
    return unwrap(response);
  },
  async returnAsset(id: string, body?: object): Promise<any> {
    const response = await api.post(`/hr/asset-issues/${id}/return`, body);
    return unwrap(response);
  },

  // ----- Talent Acquisition (auth) -----
  async getRecruitHome(): Promise<any> {
    const response = await api.get('/hr/recruit/home');
    return unwrap(response);
  },
  async recruitPeople(params?: { q?: string; ids?: string }): Promise<any[]> {
    const response = await api.get('/hr/recruit/people', { params });
    return asArray(unwrap(response));
  },
  async getCareersSettings(): Promise<any> {
    const response = await api.get('/hr/recruit/careers-settings');
    return unwrap(response);
  },
  async updateCareersSettings(body: object): Promise<any> {
    const response = await api.put('/hr/recruit/careers-settings', body);
    return unwrap(response);
  },

  async listRejectionReasons(params?: { applies_to?: string; include_inactive?: boolean }): Promise<any[]> {
    const response = await api.get('/hr/recruit/rejection-reasons', { params });
    return asArray(unwrap(response));
  },
  async createRejectionReason(body: object): Promise<any> {
    const response = await api.post('/hr/recruit/rejection-reasons', body);
    return unwrap(response);
  },
  async updateRejectionReason(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/recruit/rejection-reasons/${id}`, body);
    return unwrap(response);
  },
  async listRecruitEmailTemplates(): Promise<any[]> {
    const response = await api.get('/hr/recruit/email-templates');
    return asArray(unwrap(response));
  },
  async updateRecruitEmailTemplate(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/recruit/email-templates/${id}`, body);
    return unwrap(response);
  },
  async resetRecruitEmailTemplate(id: string): Promise<any> {
    const response = await api.post(`/hr/recruit/email-templates/${id}/reset`);
    return unwrap(response);
  },
  async runRecruitRetention(): Promise<{ anonymized: number }> {
    const response = await api.post('/hr/recruit/retention/run');
    return unwrap(response);
  },

  async listRequisitions(params?: { status?: string }): Promise<any[]> {
    const response = await api.get('/hr/recruit/requisitions', { params });
    return asArray(unwrap(response));
  },
  async pageRequisitions(params: object): Promise<PagedRows> {
    return asPaged(await api.get('/hr/recruit/requisitions', { params }));
  },
  async getRequisition(id: string): Promise<any> {
    const response = await api.get(`/hr/recruit/requisitions/${id}`);
    return unwrap(response);
  },
  async createRequisition(body: object): Promise<any> {
    const response = await api.post('/hr/recruit/requisitions', body);
    return unwrap(response);
  },
  async updateRequisition(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/recruit/requisitions/${id}`, body);
    return unwrap(response);
  },
  async submitRequisition(id: string): Promise<any> {
    const response = await api.post(`/hr/recruit/requisitions/${id}/submit`);
    return unwrap(response);
  },
  async approveRequisition(id: string, body?: { remarks?: string }): Promise<any> {
    const response = await api.post(`/hr/recruit/requisitions/${id}/approve`, body);
    return unwrap(response);
  },
  async rejectRequisition(id: string, body?: { reason_code: string; notes?: string }): Promise<any> {
    const response = await api.post(`/hr/recruit/requisitions/${id}/reject`, body);
    return unwrap(response);
  },
  async holdRequisition(id: string, hold: boolean): Promise<any> {
    const response = await api.post(`/hr/recruit/requisitions/${id}/hold`, { hold });
    return unwrap(response);
  },
  async cancelRequisition(id: string, reason: string): Promise<any> {
    const response = await api.post(`/hr/recruit/requisitions/${id}/cancel`, { reason });
    return unwrap(response);
  },

  async listRecruitJobs(params?: { status?: string; channel?: string; requisition_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/recruit/jobs', { params });
    return asArray(unwrap(response));
  },
  async pageRecruitJobs(params: object): Promise<PagedRows> {
    return asPaged(await api.get('/hr/recruit/jobs', { params }));
  },
  async getRecruitJob(id: string): Promise<any> {
    const response = await api.get(`/hr/recruit/jobs/${id}`);
    return unwrap(response);
  },
  async createRecruitJob(body: object): Promise<any> {
    const response = await api.post('/hr/recruit/jobs', body);
    return unwrap(response);
  },
  async updateRecruitJob(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/recruit/jobs/${id}`, body);
    return unwrap(response);
  },
  async publishJob(id: string): Promise<any> {
    const response = await api.post(`/hr/recruit/jobs/${id}/publish`);
    return unwrap(response);
  },
  async unpublishJob(id: string): Promise<any> {
    const response = await api.post(`/hr/recruit/jobs/${id}/unpublish`);
    return unwrap(response);
  },
  async closeJob(id: string): Promise<any> {
    const response = await api.post(`/hr/recruit/jobs/${id}/close`);
    return unwrap(response);
  },

  async listCandidates(params?: object): Promise<any[]> {
    const response = await api.get('/hr/recruit/candidates', { params });
    return asArray(unwrap(response));
  },
  async pageCandidates(params: object): Promise<PagedRows> {
    return asPaged(await api.get('/hr/recruit/candidates', { params }));
  },
  async listCandidateTags(): Promise<string[]> {
    const response = await api.get('/hr/recruit/candidates/tags');
    return asArray(unwrap(response));
  },
  async findDuplicateCandidates(params: {
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
    exclude_id?: string;
  }): Promise<any[]> {
    const response = await api.get('/hr/recruit/candidates/duplicates', { params });
    return asArray(unwrap(response));
  },
  async getCandidate(id: string): Promise<any> {
    const response = await api.get(`/hr/recruit/candidates/${id}`);
    return unwrap(response);
  },
  async createCandidate(body: object): Promise<any> {
    const response = await api.post('/hr/recruit/candidates', body);
    return unwrap(response);
  },
  async updateCandidate(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/recruit/candidates/${id}`, body);
    return unwrap(response);
  },
  async blacklistCandidate(id: string, body: { blacklisted: boolean; reason?: string }): Promise<any> {
    const response = await api.post(`/hr/recruit/candidates/${id}/blacklist`, body);
    return unwrap(response);
  },
  async mergeCandidate(id: string, intoId: string): Promise<any> {
    const response = await api.post(`/hr/recruit/candidates/${id}/merge`, { into_id: intoId });
    return unwrap(response);
  },
  async bulkCandidateTags(body: { ids: string[]; tags: string[]; mode: 'add' | 'remove' }): Promise<BulkResult> {
    const response = await api.post('/hr/recruit/candidates/bulk/tags', body);
    return unwrap(response);
  },
  async bulkCandidateBlacklist(body: { ids: string[]; blacklisted: boolean; reason?: string }): Promise<BulkResult> {
    const response = await api.post('/hr/recruit/candidates/bulk/blacklist', body);
    return unwrap(response);
  },
  async importCandidates(body: {
    rows: Array<Record<string, unknown>>;
    job_posting_id?: string;
    source?: string;
    tags?: string[];
    dry_run?: boolean;
  }): Promise<any> {
    const response = await api.post('/hr/recruit/candidates/import', body);
    return unwrap(response);
  },
  async listCandidateCommunications(id: string): Promise<any[]> {
    const response = await api.get(`/hr/recruit/candidates/${id}/communications`);
    return asArray(unwrap(response));
  },

  async listApplications(params?: { job_posting_id?: string; status?: string; candidate_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/recruit/applications', { params });
    return asArray(unwrap(response));
  },
  async pageApplications(params: object): Promise<PagedRows> {
    return asPaged(await api.get('/hr/recruit/applications', { params }));
  },
  async getApplicationBoard(params?: { job_posting_id?: string; owner_employee_id?: string; department_id?: string; q?: string }): Promise<
    Record<string, { total: number; cards: any[] }>
  > {
    const response = await api.get('/hr/recruit/applications/board', { params });
    return unwrap(response) || {};
  },
  async getApplication(id: string): Promise<any> {
    const response = await api.get(`/hr/recruit/applications/${id}`);
    return unwrap(response);
  },
  async createApplication(body: object): Promise<any> {
    const response = await api.post('/hr/recruit/applications', body);
    return unwrap(response);
  },
  async updateApplicationStatus(
    id: string,
    body: {
      status: string;
      rejection_reason_code?: string;
      rejection_notes?: string;
      screening_score?: number;
      notify_candidate?: boolean;
    }
  ): Promise<any> {
    const response = await api.post(`/hr/recruit/applications/${id}/status`, body);
    return unwrap(response);
  },
  async sendApplicationEmail(id: string, templateKey: string): Promise<any> {
    const response = await api.post(`/hr/recruit/applications/${id}/email`, { template_key: templateKey });
    return unwrap(response);
  },
  async bulkApplicationStatus(body: {
    ids: string[];
    status: string;
    rejection_reason_code?: string;
    rejection_notes?: string;
    notify_candidate?: boolean;
  }): Promise<BulkResult> {
    const response = await api.post('/hr/recruit/applications/bulk/status', body);
    return unwrap(response);
  },
  async bulkApplicationOwner(body: { ids: string[]; owner_employee_id: string | null }): Promise<BulkResult> {
    const response = await api.post('/hr/recruit/applications/bulk/owner', body);
    return unwrap(response);
  },
  async bulkApplicationAssessment(body: {
    ids: string[];
    template_id: string;
    scheduled_at?: string;
    evaluator_employee_id?: string;
    notify_candidate?: boolean;
  }): Promise<BulkResult> {
    const response = await api.post('/hr/recruit/applications/bulk/assessment', body);
    return unwrap(response);
  },

  async listInterviews(params?: { application_id?: string; status?: string; date_from?: string; date_to?: string }): Promise<any[]> {
    const response = await api.get('/hr/recruit/interviews', { params });
    return asArray(unwrap(response));
  },
  async pageInterviews(params: object): Promise<PagedRows> {
    return asPaged(await api.get('/hr/recruit/interviews', { params }));
  },
  async myInterviews(scope: 'upcoming' | 'pending_feedback' | 'all'): Promise<{ employee_id: string | null; rows: any[] }> {
    const response = await api.get('/hr/recruit/interviews/mine', { params: { scope } });
    return unwrap(response) || { employee_id: null, rows: [] };
  },
  async interviewCriteria(templateId?: string): Promise<any> {
    const response = await api.get('/hr/recruit/interviews/criteria', { params: { template_id: templateId } });
    return unwrap(response);
  },
  async interviewConflicts(body: {
    scheduled_at: string;
    duration_minutes?: number;
    panel_employee_ids?: string[];
    application_id?: string;
    exclude_id?: string;
  }): Promise<Array<{ interview_id: string; scheduled_at: string; message: string }>> {
    const response = await api.post('/hr/recruit/interviews/conflicts', body);
    return asArray(unwrap(response));
  },
  async getInterview(id: string): Promise<any> {
    const response = await api.get(`/hr/recruit/interviews/${id}`);
    return unwrap(response);
  },
  async createInterview(body: object): Promise<any> {
    const response = await api.post('/hr/recruit/interviews', body);
    return unwrap(response);
  },
  async updateInterview(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/recruit/interviews/${id}`, body);
    return unwrap(response);
  },
  async cancelInterview(id: string, body: { reason: string; notify_candidate?: boolean }): Promise<any> {
    const response = await api.post(`/hr/recruit/interviews/${id}/cancel`, body);
    return unwrap(response);
  },
  async noShowInterview(id: string, reason?: string): Promise<any> {
    const response = await api.post(`/hr/recruit/interviews/${id}/no-show`, { reason });
    return unwrap(response);
  },
  async completeInterview(id: string, body: object): Promise<any> {
    const response = await api.post(`/hr/recruit/interviews/${id}/complete`, body);
    return unwrap(response);
  },
  async submitInterviewFeedback(
    id: string,
    body: {
      ratings: Array<{ code: string; rating: number; comment?: string }>;
      recommendation: string;
      comments?: string;
    }
  ): Promise<any> {
    const response = await api.post(`/hr/recruit/interviews/${id}/feedback`, body);
    return unwrap(response);
  },

  async listOffers(params?: { status?: string; application_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/recruit/offers', { params });
    return asArray(unwrap(response));
  },
  async pageOffers(params: object): Promise<PagedRows> {
    return asPaged(await api.get('/hr/recruit/offers', { params }));
  },
  async getOffer(id: string): Promise<any> {
    const response = await api.get(`/hr/recruit/offers/${id}`);
    return unwrap(response);
  },
  async getOfferPdf(id: string): Promise<Blob> {
    const response = await api.get(`/hr/recruit/offers/${id}/pdf`, { responseType: 'blob' });
    return response.data as Blob;
  },
  async createOffer(body: object): Promise<any> {
    const response = await api.post('/hr/recruit/offers', body);
    return unwrap(response);
  },
  async updateOffer(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/recruit/offers/${id}`, body);
    return unwrap(response);
  },
  async submitOffer(id: string): Promise<any> {
    const response = await api.post(`/hr/recruit/offers/${id}/submit`);
    return unwrap(response);
  },
  async approveOffer(id: string, body?: { remarks?: string }): Promise<any> {
    const response = await api.post(`/hr/recruit/offers/${id}/approve`, body);
    return unwrap(response);
  },
  async rejectOffer(id: string, body?: { reason_code: string; notes?: string }): Promise<any> {
    const response = await api.post(`/hr/recruit/offers/${id}/reject`, body);
    return unwrap(response);
  },
  async withdrawOffer(id: string, reason: string): Promise<any> {
    const response = await api.post(`/hr/recruit/offers/${id}/withdraw`, { reason });
    return unwrap(response);
  },
  async resendOffer(id: string): Promise<any> {
    const response = await api.post(`/hr/recruit/offers/${id}/resend`);
    return unwrap(response);
  },
  async convertOffer(id: string, body?: object): Promise<any> {
    const response = await api.post(`/hr/recruit/offers/${id}/convert`, body);
    return unwrap(response);
  },

  async listOnboardingTemplates(): Promise<any[]> {
    const response = await api.get('/hr/recruit/onboarding-templates');
    return asArray(unwrap(response));
  },
  async createOnboardingTemplate(body: object): Promise<any> {
    const response = await api.post('/hr/recruit/onboarding-templates', body);
    return unwrap(response);
  },
  async updateOnboardingTemplate(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/recruit/onboarding-templates/${id}`, body);
    return unwrap(response);
  },
  async getOnboardingMeta(): Promise<{ owner_roles: string[]; task_types: string[] }> {
    const response = await api.get('/hr/recruit/onboarding/meta');
    return unwrap(response) || { owner_roles: [], task_types: [] };
  },
  async myOnboardingTasks(includeClosed = false): Promise<{ employee_id: string | null; rows: any[] }> {
    const response = await api.get('/hr/recruit/onboarding/my-tasks', { params: { include_closed: includeClosed } });
    return unwrap(response) || { employee_id: null, rows: [] };
  },
  async listOnboarding(params?: { status?: string }): Promise<any[]> {
    const response = await api.get('/hr/recruit/onboarding', { params });
    return asArray(unwrap(response));
  },
  async pageOnboarding(params: object): Promise<PagedRows> {
    return asPaged(await api.get('/hr/recruit/onboarding', { params }));
  },
  async getOnboarding(id: string): Promise<any> {
    const response = await api.get(`/hr/recruit/onboarding/${id}`);
    return unwrap(response);
  },
  async startOnboarding(body: object): Promise<any> {
    const response = await api.post('/hr/recruit/onboarding', body);
    return unwrap(response);
  },
  async completeOnboardingTask(planId: string, taskId: string, body?: { notes?: string }): Promise<any> {
    const response = await api.post(`/hr/recruit/onboarding/${planId}/tasks/${taskId}/complete`, body);
    return unwrap(response);
  },
  async skipOnboardingTask(planId: string, taskId: string, reason: string): Promise<any> {
    const response = await api.post(`/hr/recruit/onboarding/${planId}/tasks/${taskId}/skip`, { reason });
    return unwrap(response);
  },
  async reopenOnboardingTask(planId: string, taskId: string): Promise<any> {
    const response = await api.post(`/hr/recruit/onboarding/${planId}/tasks/${taskId}/reopen`);
    return unwrap(response);
  },
  async updateOnboardingTask(
    planId: string,
    taskId: string,
    body: { owner_employee_id?: string | null; due_date?: string | null; notes?: string | null }
  ): Promise<any> {
    const response = await api.put(`/hr/recruit/onboarding/${planId}/tasks/${taskId}`, body);
    return unwrap(response);
  },

  async listAssessmentTemplates(): Promise<any[]> {
    const response = await api.get('/hr/recruit/assessment-templates');
    return asArray(unwrap(response));
  },
  async createAssessmentTemplate(body: object): Promise<any> {
    const response = await api.post('/hr/recruit/assessment-templates', body);
    return unwrap(response);
  },
  async updateAssessmentTemplate(id: string, body: object): Promise<any> {
    const response = await api.put(`/hr/recruit/assessment-templates/${id}`, body);
    return unwrap(response);
  },
  async listAssessments(params?: { application_id?: string; status?: string; candidate_id?: string }): Promise<any[]> {
    const response = await api.get('/hr/recruit/assessments', { params });
    return asArray(unwrap(response));
  },
  async pageAssessments(params: object): Promise<PagedRows> {
    return asPaged(await api.get('/hr/recruit/assessments', { params }));
  },
  async getAssessment(id: string): Promise<any> {
    const response = await api.get(`/hr/recruit/assessments/${id}`);
    return unwrap(response);
  },
  async assignAssessment(body: object): Promise<any> {
    const response = await api.post('/hr/recruit/assessments', body);
    return unwrap(response);
  },
  async completeAssessment(id: string, body: object): Promise<any> {
    const response = await api.post(`/hr/recruit/assessments/${id}/complete`, body);
    return unwrap(response);
  },

  // ----- Public careers / offer / onboarding (no auth) -----
  async listPublicJobs(slug: string, q?: string): Promise<any> {
    const response = await api.get(`/hr/public/careers/${encodeURIComponent(slug)}/jobs`, { params: { q } });
    return unwrap(response);
  },
  async getPublicJob(slug: string, jobSlug: string): Promise<any> {
    const response = await api.get(
      `/hr/public/careers/${encodeURIComponent(slug)}/jobs/${encodeURIComponent(jobSlug)}`
    );
    return unwrap(response);
  },
  /** Multipart so the CV travels with the application. */
  async publicApply(slug: string, jobSlug: string, body: Record<string, unknown>, cv?: File | null): Promise<any> {
    const form = new FormData();
    for (const [k, v] of Object.entries(body)) {
      if (v == null || v === '') continue;
      form.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
    }
    if (cv) form.append('cv', cv);
    const response = await api.post(
      `/hr/public/careers/${encodeURIComponent(slug)}/jobs/${encodeURIComponent(jobSlug)}/apply`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return unwrap(response);
  },
  async getPublicOffer(token: string): Promise<any> {
    const response = await api.get(`/hr/public/offers/${encodeURIComponent(token)}`);
    return unwrap(response);
  },
  async getPublicOfferPdf(token: string): Promise<Blob> {
    const response = await api.get(`/hr/public/offers/${encodeURIComponent(token)}/pdf`, { responseType: 'blob' });
    return response.data as Blob;
  },
  async respondPublicOffer(token: string, body: { accept: boolean; notes?: string }): Promise<any> {
    const response = await api.post(`/hr/public/offers/${encodeURIComponent(token)}/respond`, body);
    return unwrap(response);
  },
  async getPublicOnboarding(token: string): Promise<any> {
    const response = await api.get(`/hr/public/onboarding/${encodeURIComponent(token)}`);
    return unwrap(response);
  },
  async acknowledgePublicOnboardingTask(token: string, taskId: string): Promise<any> {
    const response = await api.post(
      `/hr/public/onboarding/${encodeURIComponent(token)}/tasks/${encodeURIComponent(taskId)}/acknowledge`
    );
    return unwrap(response);
  },
  async uploadPublicOnboardingTask(token: string, taskId: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(
      `/hr/public/onboarding/${encodeURIComponent(token)}/tasks/${encodeURIComponent(taskId)}/upload`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return unwrap(response);
  },
};

export default hrmsService;
